import { MouseEvent, useLayoutEffect, useState } from 'react';
import './Ripple.css';

export type RippleProps = {
  duration?: number;
  spanClassName?: string;
  color?: string;
};

const useDebouncedRippleCleanUp = (
  duration: number,
  cleanUpFunction: () => void
): void => {
  useLayoutEffect(() => {
    let bounce: NodeJS.Timeout | undefined = undefined;

    clearTimeout(bounce);
    bounce = setTimeout(() => {
      cleanUpFunction();
      clearTimeout(bounce);
    }, duration);

    return () => clearTimeout(bounce);
  }, [duration, cleanUpFunction]);
};

export const Ripple = ({
  duration = 800,
  spanClassName,
  color
}: RippleProps) => {
  const [rippleArray, setRippleArray] = useState<
    {
      x: number;
      y: number;
      size: number;
    }[]
  >([]);

  useDebouncedRippleCleanUp(duration, () => {
    setRippleArray([]);
  });

  const addRipple = (event: MouseEvent) => {
    const rippleContainer = event.currentTarget.getBoundingClientRect();

    const size =
      rippleContainer.width > rippleContainer.height
        ? rippleContainer.width
        : rippleContainer.height;
    const x = event.clientX - rippleContainer.x - size / 2;
    const y = event.clientY - rippleContainer.y - size / 2;

    setRippleArray([...rippleArray, { x, y, size }]);
  };

  return (
    <div
      className="ripple-container"
      onMouseDown={addRipple}
      data-component="ripple"
    >
      {rippleArray.map((ripple, index) => (
        <span
          className={spanClassName}
          key={index}
          style={{
            background: color,
            animationDuration: `${duration}ms`,
            top: ripple.y,
            left: ripple.x,
            width: ripple.size,
            height: ripple.size
          }}
        />
      ))}
    </div>
  );
};
